import { supabase } from './supabase';

const API_ENDPOINT = '/api/vision-board-page-time';
const TABLE_NAME = 'vision_board_page_time';
const VISION_BOARD_PAGE_NAME = 'vision-board';

export type VisionBoardPageTimeExitType =
  | 'unload'
  | 'refresh'
  | 'route_change'
  | 'tab_hidden';

export interface VisionBoardPageTimeStartPayload {
  id: string;
  visitorId: string;
  sessionId: string;
  userId?: string | null;
  page?: string;
  enteredAt: string;
}

export interface VisionBoardPageTimeCompletePayload
  extends VisionBoardPageTimeStartPayload {
  exitedAt: string;
  durationSeconds: number;
  exitType: VisionBoardPageTimeExitType;
}

export interface VisionBoardPageTimeEntry {
  id: string;
  visitor_id: string;
  user_id: string | null;
  session_id: string;
  page: string;
  entered_at: string;
  exited_at: string | null;
  duration_seconds: number | null;
  exit_type: string | null;
  created_at: string;
  display_name?: string | null;
  is_admin?: boolean;
}

const toIsoString = (value: string | Date): string => {
  if (typeof value === 'string') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toISOString();
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  return new Date().toISOString();
};

const defaultPage = (page?: string) =>
  (page && page.trim()) || VISION_BOARD_PAGE_NAME;

const generateFallbackId = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.random() * 16;
    const value = char === 'x' ? Math.floor(rand) : (Math.floor(rand) & 0x3) | 0x8;
    return value.toString(16);
  });

export const generateVisionBoardPageTimeId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return generateFallbackId();
};

const sendBeaconIfAvailable = (body: Record<string, unknown>): boolean => {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.sendBeacon !== 'function'
  ) {
    return false;
  }

  try {
    const blob = new Blob([JSON.stringify(body)], {
      type: 'application/json',
    });
    return navigator.sendBeacon(API_ENDPOINT, blob);
  } catch {
    return false;
  }
};

async function postJson(body: Record<string, unknown>) {
  const response = await fetch(API_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to call vision board page time API');
  }

  return response;
}

async function fallbackStartWithSupabase(
  payload: VisionBoardPageTimeStartPayload
) {
  await supabase.from(TABLE_NAME).upsert(
    [
      {
        id: payload.id,
        visitor_id: payload.visitorId,
        session_id: payload.sessionId,
        user_id: payload.userId ?? null,
        page: defaultPage(payload.page),
        entered_at: toIsoString(payload.enteredAt),
      },
    ],
    { onConflict: 'id', ignoreDuplicates: true }
  );
}

async function fallbackCompleteWithSupabase(
  payload: VisionBoardPageTimeCompletePayload
) {
  await supabase.from(TABLE_NAME).upsert(
    [
      {
        id: payload.id,
        visitor_id: payload.visitorId,
        session_id: payload.sessionId,
        user_id: payload.userId ?? null,
        page: defaultPage(payload.page),
        entered_at: toIsoString(payload.enteredAt),
        exited_at: toIsoString(payload.exitedAt),
        duration_seconds: Math.max(0, Math.round(payload.durationSeconds)),
        exit_type: payload.exitType,
      },
    ],
    { onConflict: 'id' }
  );
}

export async function startVisionBoardPageTimeEntry(
  payload: VisionBoardPageTimeStartPayload
): Promise<void> {
  const body = {
    action: 'start',
    id: payload.id,
    visitor_id: payload.visitorId,
    session_id: payload.sessionId,
    user_id: payload.userId ?? null,
    page: defaultPage(payload.page),
    entered_at: toIsoString(payload.enteredAt),
  };

  try {
    await postJson(body);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(
        '[visionBoardPageTime] start API call failed, using supabase fallback',
        error
      );
    }
    await fallbackStartWithSupabase(payload);
  }
}

export async function completeVisionBoardPageTimeEntry(
  payload: VisionBoardPageTimeCompletePayload
): Promise<void> {
  const body = {
    action: 'end',
    id: payload.id,
    visitor_id: payload.visitorId,
    session_id: payload.sessionId,
    user_id: payload.userId ?? null,
    page: defaultPage(payload.page),
    entered_at: toIsoString(payload.enteredAt),
    exited_at: toIsoString(payload.exitedAt),
    duration_seconds: Math.max(0, Math.round(payload.durationSeconds)),
    exit_type: payload.exitType,
  };

  if (sendBeaconIfAvailable(body)) {
    return;
  }

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to end vision board page timing via API');
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(
        '[visionBoardPageTime] fetch end failed, attempting supabase fallback',
        error
      );
    }
    await fallbackCompleteWithSupabase(payload);
  }
}

export async function fetchVisionBoardPageTimeEntries(
  limit = 200
): Promise<VisionBoardPageTimeEntry[]> {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select(
      'id, visitor_id, user_id, session_id, page, entered_at, exited_at, duration_seconds, exit_type, created_at'
    )
    .order('entered_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  const rows = (data as VisionBoardPageTimeEntry[]) ?? [];
  const userIds = Array.from(
    new Set(rows.map((row) => row.user_id).filter(Boolean) as string[])
  );

  if (userIds.length > 0) {
    const [settingsResult, rolesResult] = await Promise.all([
      supabase
        .from('user_settings')
        .select('user_id, display_name')
        .in('user_id', userIds),
      supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', userIds),
    ]);

    if (!settingsResult.error && settingsResult.data) {
      const displayMap = new Map<string, string | null>();
      settingsResult.data.forEach((entry: any) => {
        if (entry?.user_id) {
          displayMap.set(entry.user_id, entry.display_name ?? null);
        }
      });

      rows.forEach((row) => {
        if (row.user_id) {
          row.display_name = displayMap.get(row.user_id) ?? null;
        }
      });
    }

    if (!rolesResult.error && rolesResult.data) {
      const adminSet = new Set(
        rolesResult.data
          .filter(
            (entry: any) =>
              typeof entry?.role === 'string' &&
              entry.role.toLowerCase() === 'admin'
          )
          .map((entry: any) => entry.user_id)
      );

      rows.forEach((row) => {
        if (row.user_id) {
          row.is_admin = adminSet.has(row.user_id);
        }
      });
    }
  }

  return rows;
}
